import { useCallback, useState } from 'react';
import type { VoiceConfig, VoiceResources } from '@lapo/core';
import type { VoiceCallingSectionState, ToastInput } from '@lapo/ui';
import type { ApiClient } from './auth/apiClient.js';
import type { AuthSession } from './auth/provider.js';
import { API_URL } from './config.js';
import { useIntegrationConfig } from './useIntegrationConfig.js';

/**
 * Owns the Voice Calling settings panel (DIAL) — the Integrations → Voice Calling settings block: the
 * Vapi config, the listed assistants/numbers (`voiceResources`, loaded alongside the config), and the
 * webhook-configured flag. Entering the Vapi key surfaces the user's assistants + numbers as dropdowns
 * and, on save, auto-configures the assistant's webhook (no UUIDs, no manual Vapi setup).
 * Behavior-preserving extraction from App.tsx: `loadVoiceResources` piggybacks the config `load` and
 * re-fires after a save (a new key surfaces new assistants/numbers), and the save toast branches on
 * `webhookError`/`webhookConfigured`.
 *
 * NOTE: this hook owns only the voice-PANEL resources/config state. The assistant's `place_call`
 * call-status watcher lives in useAssistant and is intentionally NOT moved here.
 *
 * `useIntegrationConfig` (the shared Integrations-hub state machine) lives inside the hook — its
 * `enabled` (App's `integrationsEnabled` = `!!session && !!API_URL`) and `visible` (App's
 * `settingsOpen`) gates are passed in. Other deps are `api`, `session`, and `pushToast` — never
 * reached out of App. `session` null (signed out) keeps `voiceCalling` undefined.
 */
export interface UseVoiceDeps {
  api: ApiClient;
  session: AuthSession | null;
  pushToast: (t: ToastInput) => number;
  /** Master gate for the config load — App passes `!!session && !!API_URL`. */
  integrationsEnabled: boolean;
  /** Settings visibility — every rising edge triggers a background config refresh (App's `settingsOpen`). */
  settingsOpen: boolean;
}

export interface UseVoice {
  /** The Settings-modal section object (undefined until config loads / signed out). */
  voiceCalling: VoiceCallingSectionState | undefined;
  /** Persist config (from the shared integration-config state machine). */
  onSaveVoiceCalling: (next: VoiceConfig) => Promise<void>;
}

export function useVoice({
  api,
  session,
  pushToast,
  integrationsEnabled,
  settingsOpen,
}: UseVoiceDeps): UseVoice {
  // Voice Calling settings (DIAL): enter the Vapi key → Lapo lists the user's assistants + numbers
  // (dropdowns) and auto-configures the assistant's webhook on save (no UUIDs, no manual Vapi setup).
  const [voiceResources, setVoiceResources] = useState<VoiceResources | null>(null);
  const [voiceWebhookConfigured, setVoiceWebhookConfigured] = useState(false);
  const loadVoiceResources = useCallback(() => {
    void api.getVoiceResources().then((r) => setVoiceResources(r)).catch(() => {});
  }, [api]);
  const voiceState = useIntegrationConfig<VoiceConfig>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: () => {
      loadVoiceResources();
      return api.getVoiceConfig();
    },
    save: async (next) => {
      const saved = await api.putVoiceConfig(next);
      setVoiceWebhookConfigured(!!saved.webhookConfigured);
      loadVoiceResources(); // a new key surfaces new assistants/numbers
      if (saved.webhookError) {
        pushToast({ tone: 'error', title: 'Saved, but webhook setup failed', message: saved.webhookError });
      } else if (saved.webhookConfigured) {
        pushToast({ tone: 'success', message: 'Saved — your assistant is wired up to receive call results.' });
      } else {
        pushToast({ tone: 'success', message: 'Vapi Calling settings saved.' });
      }
      return saved;
    },
    onSaveError: (err) => pushToast({ tone: 'error', title: 'Could not save', message: err.message }),
  });
  const voiceCalling: VoiceCallingSectionState | undefined =
    session && API_URL && voiceState.config
      ? {
          config: voiceState.config,
          resources: voiceResources ?? undefined,
          webhookConfigured: voiceWebhookConfigured,
          saving: voiceState.saving,
          error: voiceState.error,
        }
      : undefined;

  return {
    voiceCalling,
    onSaveVoiceCalling: voiceState.save,
  };
}
